import { faFilter, faPlus, faLaptop } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import CompanyFilter from 'components/common/CompanyFilter';
import CreateAssetModal from 'components/modals/CreateAssetModal';
import CategoryModal from 'components/modals/CategoryModal';
import AssetTable from 'components/devices/AssetTable';
import { useState, ChangeEvent, useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { fetchAssets } from 'store/assetsSlice';
import { fetchCompanies } from 'store/companySlice';

const MyAssets = () => {
  const dispatch = useDispatch<AppDispatch>(); // Add this
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<string | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get auth state and companies from Redux
  const { role } = useSelector((state: RootState) => state.auth);
  const { data: companies } = useSelector(
    (state: RootState) => state.companies
  );

  // Check if user is super admin
  const isSuperAdmin = useMemo(() => role === 'super_user', [role]);

  // Add useEffect to fetch companies if user is super admin
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(fetchCompanies(undefined));
    }
  }, [isSuperAdmin, dispatch]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value); // Update search input
  };

  // Add handleCompanySelect function
  const handleCompanySelect = (companyId: number | null) => {
    setSelectedCompanyId(companyId);
  };

  return (
    <div>
      <div className="mb-6">
        <h2
          className="mb-0"
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            letterSpacing: '-0.025em'
          }}
        >
          <FontAwesomeIcon icon={faLaptop} className="me-2 text-primary" />
          Assets
        </h2>
        <Row className="g-3 justify-content-end mb-0">
          <Col xs="auto">
            <div className="d-flex gap-2">
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                  fontWeight: '400'
                }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
              >
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{ fontSize: '0.875rem' }}
                />
                <span>Filter</span>
              </button>
              <SearchBox
                placeholder="Search by asset name or serial number"
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
              <Button
                variant="primary"
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => setOpenCreateModal(true)}
                style={{
                  fontSize: '0.875rem',
                  padding: '0.375rem 0.75rem',
                  fontWeight: '500'
                }}
              >
                Add Asset
              </Button>
            </div>
          </Col>
        </Row>

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="mb-3 mt-3">
            <div className="d-flex gap-2 flex-wrap">
              {isSuperAdmin && companies.length > 0 && (
                <CompanyFilter
                  companies={companies}
                  selectedCompanyId={selectedCompanyId}
                  onCompanySelect={handleCompanySelect}
                />
              )}
              <select
                className="form-select form-select-sm"
                value={selectedStatus || ''}
                onChange={e => setSelectedStatus(e.target.value || null)}
                style={{ minWidth: '150px', maxWidth: '150px' }}
              >
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="assigned">Assigned</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
              <select
                className="form-select form-select-sm"
                value={selectedCondition || ''}
                onChange={e => setSelectedCondition(e.target.value || null)}
                style={{ minWidth: '150px', maxWidth: '150px' }}
              >
                <option value="">All Conditions</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSelectedCompanyId(null);
                  setSelectedStatus(null);
                  setSelectedCondition(null);
                  setSelectedCategory(null);
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Asset Table */}
        <div className="mt-3">
          <AssetTable
            searchQuery={searchQuery}
            selectedCompanyId={selectedCompanyId}
            selectedStatus={selectedStatus}
            selectedCondition={selectedCondition}
          />
        </div>

        {/* Create Asset Modal - FIXED: Add refetch on close */}
        <CreateAssetModal
          show={openCreateModal}
          handleClose={() => {
            setOpenCreateModal(false);
            // ✅ FIXED: Refetch assets when modal closes to get complete data with user names
            dispatch(fetchAssets());
          }}
        />

        {/* Category Modal */}
        <CategoryModal
          show={openCategoryModal}
          handleClose={() => setOpenCategoryModal(false)}
        />
      </div>
    </div>
  );
};

export default MyAssets;
